import { UseQueryResult } from "@tanstack/react-query";
import { Form, Input, message } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { djangoClient } from "api/baseClients";
import { Document, TypeEnum } from "api/config/django-api";
import { isAxiosError } from "axios";
import SegmentedFormItem from "components/Common/SegmentedFormItem";
import {
  createFileInputProps,
  createTextContentInputProps,
  createUrlInputWithCrawlProps,
  TaskInputForm,
  TaskInputProps,
} from "components/FileAndUrlInputTypes";
import ModalWithBorders from "components/ModalWithBorders";
import { genericError } from "fixtures/globalConstants";
import { useMemo, useState } from "react";
import { trackEvent } from "utils/eventTracking/trackEvents";

// Use lowercase string literals matching SegmentedFormItem's allowed types
type AddMemoryForm = TaskInputForm & {
  title: string;
  type: "text" | "url" | "pdf"; // Changed 'file' to 'pdf'
  crawl?: boolean; // Re-added crawl property
};

const AddMemory = ({
  refetchMemoryList,
  isOpen,
  close,
}: {
  refetchMemoryList: UseQueryResult["refetch"];
  close: () => void;
  isOpen: boolean;
}) => {
  const [form] = useForm<AddMemoryForm>();

  const [isLoading, setIsLoading] = useState(false);

  const inputType = useWatch("type", form); // 'text', 'url', or 'pdf'

  const memoryTypeConfig: Partial<TaskInputProps<AddMemoryForm>> =
    useMemo(() => {
      // Map form type to API type using a map
      const typeToApiTypeMap: Record<
        AddMemoryForm["type"],
        TypeEnum | undefined
      > = {
        text: "TEXT",
        url: "URL",
        pdf: "FILE",
      };
      const apiType = inputType ? typeToApiTypeMap[inputType] : undefined;

      switch (apiType) {
        case "URL":
          return createUrlInputWithCrawlProps();
        case "FILE":
          // Pass the correct arguments object
          return createFileInputProps({ label: "File", accept: ".pdf" });
        case "TEXT":
          return createTextContentInputProps();
        // Add a default case to handle potential undefined initial state
        default:
          // Default to text input config if type is initially undefined or invalid
          return createTextContentInputProps();
      }
    }, [inputType]);

  const closeModal = () => {
    close();
    form.resetFields();
  };

  const createMemory = async ({
    description,
    title,
    crawl,
    type, // type is lowercase here: 'text', 'url', 'pdf'
    file,
    url,
  }: AddMemoryForm) => {
    setIsLoading(true);
    let body: Partial<Document> | FormData;

    // Map form type to API type using a map
    const typeToApiTypeMap: Record<AddMemoryForm["type"], TypeEnum> = {
      text: "TEXT",
      url: "URL",
      pdf: "FILE",
    };
    const apiType = typeToApiTypeMap[type];

    switch (apiType) {
      case "URL":
        body = {
          crawl,
          title,
          type: apiType,
          url,
        };
        break;
      case "FILE":
        body = new FormData();
        body.append("title", title);
        body.append("type", apiType);
        // Check if file is the expected array structure and has the File object
        if (
          Array.isArray(file) &&
          file.length > 0 &&
          file[0].originFileObj instanceof File
        ) {
          const actualFile = file[0].originFileObj as File;
          // Debug: Log the file object before appending (Keep for now)

          body.append("file", actualFile); // Append the actual File object
          body.append("name", actualFile.name); // Append the actual file name
        } else {
          console.error(
            "File object received in createMemory is not in the expected format:",
            file
          );
          // Handle the error appropriately, maybe show a message
        }
        break;
      case "TEXT":
        body = {
          title,
          type: apiType,
          text_content: description,
        };
        break;
    }

    try {
      await djangoClient.v1_documents_create(null, body as Document);
      await refetchMemoryList();
      closeModal();
      trackEvent("added_memory", { type: apiType });
      message.success("New memory created");
    } catch (error) {
      if (isAxiosError(error) && error.response?.data[0]) {
        console.error(error);
        message.error(error.response?.data[0]);
      } else {
        genericError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWithBorders
      title="Add New Memory"
      okButtonProps={{ loading: isLoading }}
      onOk={() => form.submit()}
      onCancel={closeModal}
      okText="Add Memory"
      open={isOpen}
    >
      <Form
        onFinish={(values) => createMemory(values)}
        id="create-memory-form"
        initialValues={{ type: "text", title: "" }}
        layout="vertical"
        form={form}
        requiredMark={false}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="My new memory" />
        </Form.Item>
        <SegmentedFormItem
          formItemProps={{
            rules: [{ required: true }],
            label: "Type",
            name: "type",
          }}
          options={["text", "url", "pdf"]}
        />
        {/* Conditionally render URL input fields */}
        {inputType === "url" &&
          memoryTypeConfig.label &&
          memoryTypeConfig.children && (
            <>
              <Form.Item
                label={memoryTypeConfig.label}
                name="url" // Assign name directly here
                rules={[{ required: true, message: "Please input a URL!" }]}
              >
                {/* Render only the Input part from the config children */}
                {/* Assuming the Input is the first child in the fragment */}
                {
                  (memoryTypeConfig.children as React.ReactElement)?.props
                    .children[0]
                }
              </Form.Item>
              {/* Render the crawl options separately */}
              {/* Assuming the crawl Form.Item is the second child */}
              {
                (memoryTypeConfig.children as React.ReactElement)?.props
                  .children[1]
              }
            </>
          )}
        {/* Render other input types normally */}
        {inputType !== "url" && (
          <Form.Item
            {...memoryTypeConfig}
            rules={[
              {
                required: true,
              },
            ]}
          />
        )}
      </Form>
    </ModalWithBorders>
  );
};

export default AddMemory;
